import React, { useEffect, useMemo, useState } from 'react';
import PageWrapper from '../../components/PageWrapper';
import { Typography, Box, Avatar, IconButton, DialogContent, DialogTitle, Dialog, Button, DialogActions, Snackbar, Alert, Switch, FormControlLabel, Divider, LinearProgress } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProductInfoQuery, useProductDeleteMutation, useProductUpdateMutation, useProductImageUploadMutation, useProductImageDeleteMutation, useProductDocumentUploadMutation, useProductDocumentDeleteMutation, useProductDocumentListQuery, useProductImagesReorderMutation } from '../../services/products';
import useAuth, { useAuthEffects } from '../../components/Auth';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EditProductDialog from './components/EditProductDialog';
import { Product, ProductDocument } from './types';
import placeholderImage from '../../assets/product-placeholder.png';
import FileDropZone from '../../components/FileDropZone';
import { FileItem } from '../../components/FileItem';
import { SimpleImageList } from '../../components/SimpleImageList';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useGetCompanyVendorProfileQuery } from '../../services/vendorProfile';
import { Currencies, CURRENCY_SYMBOLS } from '../../types';

const ProductView = () => {
	useAuthEffects();
	const {id} = useParams();
	const { data: product, isError, refetch: refetchProduct, isLoading: isProductLoading } = useProductInfoQuery(Number(id));
	const { data: productDocuments } = useProductDocumentListQuery(Number(id));
	const [deleteProduct, { isSuccess: isDeleteSuccess, error: deleteError }] = useProductDeleteMutation();
	const [updateProduct, { isSuccess: isUpdateSuccess }] = useProductUpdateMutation();
	const [uploadProductImage, { isLoading: isUploadImageLoading }] = useProductImageUploadMutation({});
	const [uploadProductDocument, { isLoading: isUploadDocumentLoading }] = useProductDocumentUploadMutation({});
	const [deleteProductImage] = useProductImageDeleteMutation();
	const [deleteProductDocument] = useProductDocumentDeleteMutation();
	const [productImagesReorderMutation] = useProductImagesReorderMutation();
	const navigate = useNavigate();
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const {userProfile} = useAuth();
	const {data: vendorProfile} = useGetCompanyVendorProfileQuery(undefined, {skip: !userProfile});

	// const [firstImage] = useMemo(() => product?.images.length ? product?.images : [], [product]);
	const totalImagesCount = useMemo(() => product?.images.length ?? 0, [product]);

	useEffect(() => {
		if (deleteError) {
			setErrorMessage((deleteError as any)?.data?.message ?? 'Failed to delete product');
		}
	}, [deleteError]);

	useEffect(() => {
		if (isDeleteSuccess) {
			navigate('/products');
		}
	}, [isDeleteSuccess]);

	const handleDeleteProduct = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		deleteProduct(Number(id));
		setIsDeleteDialogOpen(false);
	};

	const handleEditProduct = (payload: Partial<Product>) => {
		updateProduct({ ...payload, id: Number(id) }).unwrap().then(() => {
			setEditDialogOpen(false);
		}).catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to update product');
		});
	};

	const handleImageUpload = (file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		uploadProductImage({id: Number(id), formData});
	};

	const handleDocumentUpload = (file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		uploadProductDocument({id: Number(id), formData}).unwrap().then(() => {
			refetchProduct(); // TODO: this is a hacky way to refresh the product documents list, because uploading a document might change instant delivery status
		});
	};

	const handleReorderImages = (reorderedItems: {id: number, rank: number}[]) => {
		productImagesReorderMutation(reorderedItems).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to reorder images');
		});
	};

	useEffect(() => {
		if (isUpdateSuccess) {
			refetchProduct();
		}
	}, [isUpdateSuccess]);

	if (isError || !product) {
		return <PageWrapper isLoading={isProductLoading} noScroll>
			<Box flex={1} display='flex' flexDirection='column' justifyContent='center' alignItems='center' gap={2}>
				<Typography variant='h2'>Product not found</Typography>
				<Button component={Link} to='/products'>Back to products</Button>
			</Box>
		</PageWrapper>;
	}

	return <PageWrapper maxWidth='md'>
		<Box display='flex' flexDirection='column' gap={2} mt={2}>
			<div>
				<Button startIcon={<ChevronLeftIcon />} component={Link} to='/products'>Back to products</Button>
			</div>
			{product && <>
				<Box gap={2} p={2} display='flex' alignItems='center'>
					<Box position='relative'>
						<Avatar src={product.imgUrl} variant='square' sx={{width: 96, height: 96}}>
							<img src={placeholderImage} alt={product.name} width={96} height={96} />
						</Avatar>
						{/* {firstImage && <IconButton onClick={() => deleteProductImage(firstImage?.id)} sx={{position: 'absolute', bottom: -20, right: -20}}>
							<DeleteIcon />
						</IconButton>} */}
					</Box>
					<Typography variant='h4' flexGrow={1}>{product.name}</Typography>
					<IconButton onClick={() => setEditDialogOpen(true)}>
						<EditIcon />
					</IconButton>
				</Box>
				<Box display='flex' gap={2} p={2} flexDirection='column'>
					<div>
						<Typography variant='body2'>SKU</Typography>
						<Typography variant='body1'>{product?.sku ?? '-'}</Typography>
					</div>
					{!product.isBundle && <div>
						<Typography variant='body2'>Price per unit</Typography>
						<Typography variant='body1'>{CURRENCY_SYMBOLS[vendorProfile?.currency as Currencies]}{product?.pricePerUnit ?? '-'}</Typography>
					</div>}
					<div>
						<Typography variant='body2'>UOM</Typography>
						<Typography variant='body1'>{product?.uom ?? '-'}</Typography>
					</div>
					<div>
						<Typography variant='body2'>Additional information</Typography>
						<Typography variant='body1' sx={{whiteSpace: 'pre-wrap'}}>{product?.description ?? '-'}</Typography>
					</div>
					<div>
						<Typography variant='body2'>Notes</Typography>
						<Typography variant='body1'>{product?.notes ?? '-'}</Typography>
					</div>
					<div>
						<Typography variant='body2'>Update image</Typography>
						<SimpleImageList 
							images={product?.images.map((image) => ({imgUrl: image.imgUrl, title: '', id: image.id})) ?? []} 
							onDeleteImageClick={(imageId) => deleteProductImage(imageId)}
							onReorderSubmit={(reorderedItems) => handleReorderImages(reorderedItems)}
							sx={{mb: 2}}
						/>
						{isUploadImageLoading && <LinearProgress />}
						<FileDropZone onFileUpload={handleImageUpload} disabled={totalImagesCount >= 3} id='file-upload-image' accept='image/*' />
					</div>
					<div>
						<Typography variant='body2'>Documents</Typography>
						{productDocuments?.length ?<Box display='flex' flexDirection='row' gap={1} my={1} flexWrap='wrap'>
							{productDocuments?.map((document: ProductDocument) => (
								<FileItem key={document.id} document={document} onDelete={() => deleteProductDocument(document.id)} />
							))}
						</Box> : <Typography variant='body1'>No documents</Typography>}
						{isUploadDocumentLoading && <LinearProgress />}
						<FileDropZone onFileUpload={handleDocumentUpload} id='file-upload-document' accept='application/pdf' />
					</div>
					<div>
						<Typography variant='body2'>Bundle info</Typography>
						<FormControlLabel
							control={<Switch	
								checked={product?.isBundle}
								onChange={(_, value) => handleEditProduct({isBundle: value})}
								sx={{userSelect: 'none'}}
							/>}
							label='Is bundle'
						/>
					</div>
					<div>
						<Typography variant='body2'>Shipping</Typography>
						<FormControlLabel
							control={<Switch	
								checked={product?.digitalDelivery}
								onChange={(_, value) => handleEditProduct({digitalDelivery: value})}
								sx={{userSelect: 'none'}}
							/>}
							label='Digital delivery'
						/>
					</div>
				</Box>
				<Divider />
				<Box>
					<Button variant='text' color='error' onClick={() => setIsDeleteDialogOpen(true)} startIcon={<DeleteIcon />}>
						Delete Product
					</Button>
				</Box>
			</>}
		</Box>
		{product && <EditProductDialog 
			product={product} 
			open={editDialogOpen} 
			onClose={() => setEditDialogOpen(false)} 
			onSubmit={handleEditProduct} 
		/>}
		<Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
			<form onSubmit={handleDeleteProduct}>
				<DialogTitle>Delete product</DialogTitle>
				<DialogContent>
					<Typography>Are you sure you want to delete <strong>{product?.name}</strong>?</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
					<Button type='submit' variant='contained' color='error'>Delete</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Snackbar open={Boolean(errorMessage)} autoHideDuration={6000} onClose={() => setErrorMessage(null)}>
			<Alert severity='error'>{errorMessage}</Alert>
		</Snackbar>
	</PageWrapper>;
};

export default ProductView;
